// Base Libs
import { shallowEqual } from "react-redux";
// Styles
import "./AssistedMultipleSelectQuestionOption.less";
// Feature commons
import { useAppSelector } from "app/hooks";
import { selectCanUserViewSTATopics } from "features/auth/currentUserSlice";
import { ScoreTag } from "features/common/ScoreTag";
import { useStrings } from "features/i18n/useStrings";
// Feature slices
import {
    getQuestionTotalScore,
    selectAnswerOption,
    selectAssistedAnswerIdsForMSQ,
    selectCurrentAnswerIdsForMSQ,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import {
    selectExcludedTopicsForAnswer,
    selectExistingTopicsForAnswer,
    selectTopicDetails,
    selectOptionLevel,
} from "features/scorable/scorableSlice";

// Locals
import {
    MultipleSelectQuestionDropdownOption,
    MultipleSelectQuestionOption,
} from "features/qualityForm/common/questionComponents/MultipleSelectQuestionOptions";
import { TopicSummary, TopicList } from "./AssistedMCQOption";
import {
    GuxDropdownMulti,
    GuxIcon,
    GuxListboxMulti,
    GuxTooltip,
} from "genesys-spark-components-react";
import strings from "features/qualityForm/common/questionComponents/CommonQuestionComponents.strings";
import { getScoreValueAriaLabel } from "util/componentUtil";

export function AssistedMultipleSelectQuestionOptionCheckbox({
    questionId,
    answerOptionId,
    selected,
    disabled,
    onSelected,
    hideScore = false,
}: {
    questionId: string;
    answerOptionId: string;
    selected: boolean;
    disabled: boolean;
    onSelected: (s: string) => void;
    hideScore?: boolean;
}) {
    const t = useStrings(strings);
    const value = useAppSelector((state) => {
        return selectAnswerOption(state, answerOptionId, true)?.value ?? "";
    });
    const canViewTopics = useAppSelector(selectCanUserViewSTATopics);
    const existingTopics = useAppSelector((state) => {
        const topics = selectExistingTopicsForAnswer(
            answerOptionId,
            state,
            true
        );
        return topics && topics.length > 0 ? topics : undefined;
    }, shallowEqual);
    const excludedTopics = useAppSelector((state) => {
        const topics = selectExcludedTopicsForAnswer(
            answerOptionId,
            state,
            true
        );
        return topics && topics.length > 0 ? topics : undefined;
    }, shallowEqual);
    const existingTopicPhraseDetails = useAppSelector((state) => {
        if (existingTopics) {
            return selectTopicDetails(state, existingTopics);
        }
    }, shallowEqual);
    const questionLevel = useAppSelector((state) => {
        return selectOptionLevel(state, questionId, answerOptionId);
    });
    return (
        <div className="AssistedMultipleSelectQuestionOption">
            <div
                className={`MultipleSelectQuestionOption ${
                    selected ? "selected" : ""
                }`}
            >
                <MultipleSelectQuestionOption
                    questionId={questionId}
                    multipleSelectOptionId={answerOptionId}
                    disabled={disabled}
                    selected={selected}
                    onSelected={onSelected}
                    hideScore={true}
                ></MultipleSelectQuestionOption>
                <div className="assisted-answer-details">
                    {canViewTopics && (
                        <div>
                            <TopicSummary
                                existingTopics={existingTopics}
                                excludedTopics={excludedTopics}
                                phraseCount={
                                    existingTopicPhraseDetails?.length ?? 0
                                }
                            />
                        </div>
                    )}
                    {disabled && !hideScore && (
                        <div className="score">
                            <ScoreTag
                                mode={questionLevel}
                                ariaLabel={getScoreValueAriaLabel(
                                    t.labels,
                                    questionLevel,
                                    value ?? 0
                                )}
                            >
                                {value}
                            </ScoreTag>
                        </div>
                    )}
                </div>
            </div>
            {canViewTopics && existingTopicPhraseDetails && existingTopics && (
                <TopicList
                    hideTopicName={existingTopics?.length <= 1}
                    topicDetails={existingTopicPhraseDetails}
                />
            )}
        </div>
    );
}
export function AssistedMultipleSelectQuestionOptionsDropdown({
    questionId,
    disabled = true,
    hideScore = false,
    sortedOptions,
    onInput,
    onCollapsed: onCollapsed,
}: {
    questionId: string;
    disabled: boolean;
    hideScore: boolean;
    sortedOptions: string[];
    onInput: React.FormEventHandler<HTMLGuxDropdownMultiElement>;
    onCollapsed: () => void;
}) {
    const t = useStrings(strings);
    const answerScoreValue = useAppSelector((state) => {
        return getQuestionTotalScore(state, questionId, true);
    });
    const selectedAnswerOptionIds = useAppSelector((state) => {
        return selectCurrentAnswerIdsForMSQ(state, questionId);
    });
    const assistedAnswerIds = useAppSelector((state) => {
        return selectAssistedAnswerIdsForMSQ(state, questionId);
    });
    const canViewTopics = useAppSelector(selectCanUserViewSTATopics);
    const existingTopics = useAppSelector((state) => {
        // iterate through every key in assistedAnswerIds
        const allDetectedTopics: string[] = [];
        for (const answerOptionId in assistedAnswerIds) {
            const topics = selectExistingTopicsForAnswer(
                answerOptionId,
                state,
                true
            );
            if (topics) {
                allDetectedTopics.push(...topics);
            }
        }
        const uniqueTopics = [...new Set(allDetectedTopics)];
        return uniqueTopics.length > 0 ? uniqueTopics : undefined;
    }, shallowEqual);
    const excludedTopics = useAppSelector((state) => {
        const allExcludedTopics: string[] = [];
        for (const answerOptionId in assistedAnswerIds) {
            const topics = selectExcludedTopicsForAnswer(
                answerOptionId,
                state,
                true
            );
            if (topics) {
                allExcludedTopics.push(...topics);
            }
        }
        const uniqueTopics = [...new Set(allExcludedTopics)];
        return uniqueTopics.length > 0 ? uniqueTopics : undefined;
    }, shallowEqual);
    const existingTopicPhraseDetails = useAppSelector((state) => {
        if (existingTopics) {
            return selectTopicDetails(state, existingTopics);
        }
    }, shallowEqual);

    return (
        <div className="AssistedMultipleSelectQuestion">
            <div className="evaluation-assistance-quote">
                <GuxIcon
                    iconName="quote-left"
                    data-testid="evaluation-assistance-quote"
                    screenreader-text={t.labels.assistedSelection}
                />
                <GuxTooltip>{t.labels.assistedSelection}</GuxTooltip>
            </div>
            <div className="MultipleSelectQuestionDropdownGroup">
                <GuxDropdownMulti
                    data-testid="multiple-select-dropdown"
                    filterType="starts-with"
                    placeholder={t.labels.multipleSelectPlaceholder}
                    value={(selectedAnswerOptionIds || []).join(",")}
                    onInput={(e) => onInput(e)}
                    onGuxcollapsed={() => onCollapsed()}
                >
                    <GuxListboxMulti
                        aria-label={t.labels.multipleSelectAriaLabel}
                    >
                        {sortedOptions.map((mSelectOptId) => {
                            if (mSelectOptId) {
                                return (
                                    <MultipleSelectQuestionDropdownOption
                                        questionId={questionId}
                                        multipleSelectOptionId={mSelectOptId}
                                        key={mSelectOptId}
                                        showScore={disabled && !hideScore}
                                    />
                                );
                            }
                        })}
                    </GuxListboxMulti>
                </GuxDropdownMulti>
                <div className="assisted-answer-details">
                    {canViewTopics && (
                        <div>
                            <TopicSummary
                                existingTopics={existingTopics}
                                excludedTopics={excludedTopics}
                                phraseCount={
                                    existingTopicPhraseDetails?.length ?? 0
                                }
                            />
                        </div>
                    )}
                    {disabled && !hideScore && (
                        <ScoreTag
                            mode="default"
                            selected={
                                (selectedAnswerOptionIds?.length ?? 0) > 0
                            }
                            ariaLabel={getScoreValueAriaLabel(
                                t.labels,
                                "default",
                                answerScoreValue
                            )}
                        >
                            {answerScoreValue}
                        </ScoreTag>
                    )}
                </div>
            </div>
        </div>
    );
}
